import { cn } from '@/lib/utils';
import { uniqBy } from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';

import {
    ChainlitContext,
    IThread,
    threadHistoryState,
    useChatMessages,
    useChatSession
} from '@chainlit/react-client';

import { FluidHoverGroup } from '@/components/ui/fluid-hover-group';
import {
    SidebarContent,
    SidebarGroup,
    SidebarMenu
} from '@/components/ui/sidebar';

import { ThreadList } from './ThreadList';

const BATCH_SIZE = 35;
let _scrollTop = 0;
let _hasHandledFirstInteraction = false; // Module-level flag to persist across component remounts
let _hasInitiallyFetched = false; // Module-level flag for initial thread fetch
let _isFetchingThreads = false; // Module-level flag to prevent concurrent fetches

export function ThreadHistory() {
    const navigate = useNavigate();
    const { idToResume } = useChatSession();
    const scrollRef = useRef<HTMLDivElement>(null);
    const apiClient = useContext(ChainlitContext);
    const { firstInteraction, messages, threadId } = useChatMessages();
    const [threadHistory, setThreadHistory] =
        useRecoilState(threadHistoryState);
    const [error, setError] = useState<string>();
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [shouldLoadMore, setShouldLoadMore] = useState(false);
    const [isScrolled, setIsScrolled] = useState(_scrollTop > 0);

    // Restore scroll position
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = _scrollTop;
        }
    }, []);

    // Reset the first interaction flag when a new chat is started
    // (when firstInteraction becomes undefined after clear())
    useEffect(() => {
        if (firstInteraction === undefined) {
            _hasHandledFirstInteraction = false;
        }
    }, [firstInteraction]);

    // Handle first interaction - add new thread to sidebar immediately
    useEffect(() => {
        const handleFirstInteraction = async () => {
            // Skip if we've already handled a first interaction
            if (_hasHandledFirstInteraction) return;

            // Skip if no firstInteraction yet
            if (!firstInteraction) return;

            const isActualResume =
                firstInteraction === 'resume' &&
                messages[0]?.output.toLowerCase() !== 'resume';

            // Set the flag immediately to prevent duplicate handling, even if we skip the resume case
            _hasHandledFirstInteraction = true;

            // Skip for resume case, but flag is already set
            if (isActualResume) return;

            // For new chats, add the thread directly to the sidebar
            if (threadId && firstInteraction !== 'resume') {
                // Create a new thread object to add to the list
                const newThread: IThread = {
                    id: threadId,
                    name: firstInteraction,
                    createdAt: new Date().toISOString(),
                    steps: []
                };

                // Add the new thread at the beginning of the list
                setThreadHistory((prev) => {
                    // Check if thread already exists
                    const existingThreads = prev?.threads || [];
                    const threadExists = existingThreads.some(
                        (t) => t.id === threadId
                    );

                    if (threadExists) {
                        return prev;
                    }

                    return {
                        ...prev,
                        threads: [newThread, ...existingThreads],
                        currentThreadId: threadId
                    };
                });
            }

            // If we haven't fetched threads yet, fetch them (but only once)
            if (!_hasInitiallyFetched) {
                _hasInitiallyFetched = true;
                await fetchThreads(undefined, true, false);
            }

            const currentPage = new URL(window.location.href);
            if (threadId && currentPage.pathname === '/') {
                navigate(`/thread/${threadId}`);
            }
        };

        handleFirstInteraction();
    }, [firstInteraction, threadId]);

    // Listen for thread fork completion to refresh thread list
    useEffect(() => {
        const handleThreadForked = () => {
            // Small delay to ensure the thread and message are fully saved on backend
            setTimeout(() => {
                fetchThreads(undefined, true);
            }, 300);
        };

        window.addEventListener(
            'thread-forked-message-sent' as any,
            handleThreadForked
        );

        return () => {
            window.removeEventListener(
                'thread-forked-message-sent' as any,
                handleThreadForked
            );
        };
    }, []);

    const handleScroll = () => {
        if (!scrollRef.current) return;
        const { scrollHeight, clientHeight, scrollTop } = scrollRef.current;
        const atBottom = scrollTop + clientHeight >= scrollHeight - 10;

        _scrollTop = scrollTop;
        setIsScrolled(scrollTop > 0);
        setShouldLoadMore(atBottom);
    };

    const fetchThreads = async (
        cursor?: string | number,
        isLoadingMore = false,
        forceRefresh = false
    ) => {
        // Prevent concurrent fetches (unless it's a forced refresh like after pinning)
        if (_isFetchingThreads && !forceRefresh) return;

        try {
            _isFetchingThreads = true;
            setIsLoadingMore(!!cursor || isLoadingMore);
            setIsFetching(!cursor && !isLoadingMore);

            const { pageInfo, data } = await apiClient.listThreads(
                { first: BATCH_SIZE, cursor },
                { isPinned: false, isDeepIntelligence: false }
            );

            setError(undefined);

            // Prevent duplicate threads
            const allThreads = uniqBy(
                cursor ? threadHistory?.threads?.concat(data) : data,
                'id'
            );

            if (allThreads) {
                setThreadHistory((prev) => ({
                    ...prev,
                    pageInfo,
                    threads: allThreads
                }));
            }
        } catch (err) {
            setError(
                err instanceof Error ? err.message : 'Unknown error occurred'
            );
        } finally {
            setShouldLoadMore(false);
            setIsLoadingMore(false);
            setIsFetching(false);
            _isFetchingThreads = false;
        }
    };

    // Initial fetch - only run once globally (persists across all component instances)
    useEffect(() => {
        // Check and set flag atomically to prevent race conditions
        if (_hasInitiallyFetched) return;

        if (!isFetching && !threadHistory?.threads && !error) {
            _hasInitiallyFetched = true;
            fetchThreads();
        }
    }, [isFetching, threadHistory, error]);

    // Handle infinite scroll
    useEffect(() => {
        if (threadHistory?.pageInfo) {
            const { hasNextPage, endCursor } = threadHistory.pageInfo;

            if (shouldLoadMore && !isLoadingMore && hasNextPage && endCursor) {
                fetchThreads(endCursor);
            }
        }
    }, [shouldLoadMore, isLoadingMore, threadHistory]);

    // Refresh sidebar threads when pin/unpin status changes elsewhere in the app (organization threads page)
    useEffect(() => {
        const handleThreadPinChanged = () => {
            fetchThreads(undefined, true, true);
        };

        window.addEventListener(
            'thread-pin-changed' as any,
            handleThreadPinChanged
        );

        return () => {
            window.removeEventListener(
                'thread-pin-changed' as any,
                handleThreadPinChanged
            );
        };
    }, []);

    return (
        <>
            <div
                className={cn(
                    'top-0 right-0 left-0 z-50 absolute bg-linear-to-t from-transparent to-sidebar h-16 transition-opacity duration-200 pointer-events-none',
                    isScrolled ? 'opacity-100' : 'opacity-0'
                )}
            />
            {/* Scrollable thread list - takes remaining space */}
            <div
                className="relative flex-1 min-h-0 overflow-y-auto"
                onScroll={handleScroll}
                ref={scrollRef}
            >
                <SidebarContent>
                    <FluidHoverGroup className="w-full">
                        <SidebarGroup>
                            <SidebarMenu>
                                <ThreadList
                                    threadHistory={threadHistory}
                                    error={error}
                                    isFetching={isFetching}
                                    isLoadingMore={isLoadingMore}
                                    onThreadPinned={() => {
                                        fetchThreads(undefined, true, true);
                                    }}
                                />
                            </SidebarMenu>
                        </SidebarGroup>
                    </FluidHoverGroup>
                </SidebarContent>
            </div>
        </>
    );
}
